import type {PerkIntegrationConfigComponentProps} from '@cohort/merchants/apps';
import ConnectionPicker from '@cohort/merchants/components/connections/ConnectionPicker';
import Input from '@cohort/merchants/components/form/v2/Input';
import HighlightText from '@cohort/merchants/components/HighlightText';
import {useConnectionProfile, useConnections} from '@cohort/merchants/hooks/api/Connections';
import {useCurrentMerchant} from '@cohort/merchants/hooks/contexts/currentMerchant';
import type {PerkFormValues} from '@cohort/merchants/pages/perks/perk/edit/utils';
import {Fragment, useEffect, useState} from 'react';
import type {UseFormReturn} from 'react-hook-form';
import {useController} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

type CloseFriendConfigComponentProps = {
  formReturn: UseFormReturn<PerkFormValues>;
  connectionId: string | null;
};
const CloseFriendConfigComponent: React.FC<CloseFriendConfigComponentProps> = ({
  formReturn: {setValue, register, control},
  connectionId,
}) => {
  const merchant = useCurrentMerchant();
  const {t} = useTranslation('app-instagram', {
    keyPrefix: 'perkIntegrations.close-friends-list.configComponent',
  });

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const {data: profile} = useConnectionProfile(merchant.id, connectionId!, {
    enabled: !!connectionId,
  });
  // Set the value of the username to the input field
  useEffect(() => {
    setValue('integration.config.username', `${profile?.username}`);
  }, [profile, setValue]);

  if (!profile || !profile.username) {
    return <Fragment />;
  }
  return (
    <div className="mt-3">
      <HighlightText type="info" text={t('instructions')} />
      <Input
        type="text"
        name="integration.config.username"
        label={t('labelInstagramUsername')}
        disabled={true}
        register={register}
        control={control}
      />
    </div>
  );
};

const InstagramCloseFriendsListPerkIntegrationConfigComponent: React.FC<
  PerkIntegrationConfigComponentProps
> = ({formReturn}) => {
  const {control, register} = formReturn;
  const {field: connectionIdField} = useController({control, name: 'integration.connectionId'});
  const [showUsernameInput, setShowUsernameInput] = useState(false);
  const [connectionId, setConnectionId] = useState<string | null>(connectionIdField.value);
  const merchant = useCurrentMerchant();
  const {data: connections} = useConnections(merchant.id, {
    connectorId: ['instagram-merchant'],
  });

  useEffect(() => {
    if (connections === undefined) {
      return;
    }
    const readyConnections = connections.filter(connection => connection.status === 'ready');
    if (readyConnections.length > 0) {
      setConnectionId(readyConnections[0]?.id ?? null);
      setShowUsernameInput(true);
    }
  }, [connections, connectionIdField.value]);

  const handleConnectionModalClose = (): void => {
    // @Devs, show the Username input only when the modal is closed and the connection is ready.
    setShowUsernameInput(true);
  };

  return (
    <div className="space-y-4">
      <ConnectionPicker
        appId="instagram"
        register={register}
        control={control}
        onConnectionModalClose={handleConnectionModalClose}
      />
      {showUsernameInput && (
        <CloseFriendConfigComponent
          formReturn={formReturn}
          connectionId={connectionIdField.value ?? connectionId}
        />
      )}
    </div>
  );
};

export default InstagramCloseFriendsListPerkIntegrationConfigComponent;
